/*
Publicacion en Yapo
Autor: Benjamín León
Fecha: 2024-12-15
*/
import { useEffect, useState, useRef } from 'react'

import Brand from '../interfaces/Brand'
import Vehicle from '../interfaces/Vehicle'
import useYapoFetcher from '../hooks/useYapoFetcher'
import YapoModel from '../interfaces/YapoModel'
import YapoInputVehicle from '../interfaces/YapoInputVehicle'
import { useAuth } from '../hooks/useAuth'
import useYapoPublisher from '../hooks/useYapoPublisher'
import useBranchesFetcher from '../hooks/useBranchesFetcher'
import useLocalization from '../hooks/useLocalization'
import { Rule, validateCombos, validateFields } from '../utils/validateForm'
import { BranchDto } from '../interfaces/Branch'
import Region from '../interfaces/Region'
import VehicleBody from '../interfaces/VehicleBody'
import usePublicationDataSaver from '../hooks/usePublicationDataSaver'

import ConfirmationModal from './ConfirmationModal'
import DisplayError from './DisplayError'
import { Info } from './MessageInfo'
import { ButtonAction } from './Buttons'
import InputField from './InputField'
import { SelectField } from './SelectField'

interface YapoData {
  type: string
  body: string
  brand: string
  model: string
  year: number
  fuel: string
  gearbox: string
  comments: string
  localization: string
}

const Yapo: React.FC<{ vehicle: Vehicle; onRefresh: () => void }> = ({ vehicle, onRefresh }) => {
  const { userData } = useAuth()
  const [formData, setFormData] = useState<YapoData>({
    type: vehicle.type,
    body: vehicle.body,
    brand: vehicle.brand,
    model: vehicle.model,
    year: vehicle.year,
    fuel: vehicle.fuel,
    gearbox: vehicle.gearbox,
    // CRITICAL: Use promo.message as source, NOT vehicle.comments (internal field)
    comments: vehicle.promo?.message || ' ',
    localization: '',
  })
  const [brands, setBrands] = useState<Brand[]>([])
  const [types, setTypes] = useState<Record<string, string>>({})
  const [bodies, setBodies] = useState<VehicleBody[]>([])
  const [years, setYears] = useState<number[]>([])
  const [fuel, setFuel] = useState<Record<string, string>>({})
  const [gearbox, setGearbox] = useState<Record<string, string>>({})
  const [hasUserSelectedRegion, setHasUserSelectedRegion] = useState<boolean>(false)

  const {
    yapoBrands,
    errorYapoBrands,
    yapoTypes,
    errorYapoTypes,
    yapoModels,
    errorYapoModels,
    yapoBodies,
    errorYapoBodies,
    yapoYears,
    errorYapoYears,
    yapoFuel,
    errorYapoFuel,
    yapoGearbox,
    errorYapoGearbox,
  } = useYapoFetcher()

  const { yapoPublish, errorYapoPublish, yapoSetPublished, errorYapoSetPublished } = useYapoPublisher()
  const {
    savePublicationData,
    loadPublicationData,
    loading: savingLoading,
    error: saveError,
    clearError,
  } = usePublicationDataSaver()

  const [allBrands, setAllBrands] = useState<Brand[]>([])
  const [allBodies, setAllBodies] = useState<VehicleBody[]>([])
  const [models, setModels] = useState<YapoModel[]>([])
  const [infoMessage, setInfoMessage] = useState<string>('')
  const { fetchBranchDetail } = useBranchesFetcher()
  const [faltaMsg, setFaltaMsg] = useState<string>('')
  const [saveMessage, setSaveMessage] = useState<string>('')
  const { fetchRegionDetail, errorRegion } = useLocalization()
  const [disabled, setDisabled] = useState<boolean>(false)
  const [branch, setBranch] = useState<BranchDto>()
  const [branchRegionId, setBranchRegionId] = useState<string>('')
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [confirmationModalOpen, setConfirmationModalOpen] = useState<boolean>(false)
  const [regions, setRegions] = useState<Region[]>([])
  const [isDraftLoading, setIsDraftLoading] = useState<boolean>(false)
  const [minimumTimeElapsed, setMinimumTimeElapsed] = useState<boolean>(false)
  const [externalApisComplete, setExternalApisComplete] = useState<boolean>(false)
  const { fetchMunicipalities, errorMunicipalities } = useLocalization()
  const tempLocalizationRef = useRef<string | null>(null)

  useEffect(() => {
    const fetchBrandsAsync = async () => {
      const toutBrands = await yapoBrands()

      if (toutBrands.length > 0) {
        setAllBrands(toutBrands)
      }
    }
    const fetchTypesAsync = async () => {
      const allTypes = await yapoTypes()
      setTypes(allTypes)
    }
    const fetchBodiesAsync = async () => {
      const allBodies = await yapoBodies()
      setAllBodies(allBodies)
    }
    const fetchYearsAsync = async () => {
      const years = await yapoYears()
      setYears(years)
    }
    const fetchFuelAsync = async () => {
      const fuel = await yapoFuel()
      setFuel(fuel)
    }
    const fetchGearboxAsync = async () => {
      const data = await yapoGearbox()
      setGearbox(data)
    }
    const fetchBranchAsync = async () => {
      if (!vehicle.branch) {
        const msg = 'Asigna una sucursal al vehículo.'
        setFaltaMsg(msg)
        setDisabled(true)

        return
      }
      const branch = await fetchBranchDetail(vehicle.branch)

      if (!branch) {
        // raro
        setFaltaMsg('Sucursal no encontrada.')
        setDisabled(true)

        return
      }

      let msg = null
      const comuna = branch?.municipality_format ?? ''

      if (!comuna) {
        msg = 'una comuna'
      } else if (!branch.address) {
        msg = 'una dirección'
      } else if (!branch.phone) {
        msg = 'un teléfono'
      } else if (!branch.whatsapp) {
        msg = 'un nro. WhatAapp'
      }

      if (msg) {
        const msgAux = `Por favor, asigna ${msg} a la sucursal.`
        setFaltaMsg(msgAux)
        setDisabled(true)

        return
      }
      setBranch(branch)
      //setBranchRegionId(comuna);

      const regs = await fetchMunicipalities()

      if (regs) {
        setRegions(regs)
        // Buscamos todas las regiones hasta encontrat aquella
        // que su comuna (texto) es igual a la del branch
        const region = regs.find((r) => r.name === comuna)

        if (region) {
          // Only set default branch region if no draft data has been loaded
          // (avoid overriding draft-loaded localization)
          setBranchRegionId((prev) => prev || region.regionId)
        }
      }
    }

    const loadAllData = async () => {
      await Promise.all([
        fetchBrandsAsync(),
        fetchTypesAsync(),
        fetchBodiesAsync(),
        fetchYearsAsync(),
        fetchFuelAsync(),
        fetchGearboxAsync(),
        fetchBranchAsync(),
      ])
    }

    const initializeComponentData = async () => {
      // Show loading message immediately and start minimum timer
      setIsDraftLoading(true)
      setTimeout(() => setMinimumTimeElapsed(true), 1500)

      // SEQUENTIAL LOADING: Load dependencies first, then draft data
      // Step 1: Load all external APIs and their data (regions, models, etc.)
      await loadAllData()

      // Step 2: Now that regions/models are loaded, apply draft data
      await autoLoadDraftData()

      setExternalApisComplete(true)
    }

    initializeComponentData()

    let sw = true

    if (!userData?.organization?.external?.yapo?.enabled) {
      setFaltaMsg('Su organización no está autoriada para publicar en Yapo.')
      sw = false
    } else if (!userData?.organization?.external?.yapo?.importApiKey) {
      setFaltaMsg("Falta la 'key' para publicar en Yapo.")
      sw = false
    } else if (!userData?.organization?.external?.yapo?.userId) {
      setFaltaMsg("Falta el 'userId' para publicar en Yapo.")
      sw = false
    }

    if (!sw) {
      setDisabled(true)
    }
    // Colocamos un numero grande para asegurarnos de hacer scroll hasta el final
    window.scrollTo({ top: 100000, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    if (formData && allBrands) {
      const brands = allBrands.filter((b) => b.type === formData.type)
      setBrands(brands)
    }
  }, [formData.type, allBrands])

  // Cuando cambia el tipo (auto, moto, etc)
  useEffect(() => {
    const bodiesPerType = allBodies.filter((b) => b.type === formData.type)
    setBodies(bodiesPerType)
  }, [formData.type, allBodies])

  // Convert full enum type to simplified type for Yapo API
  const getYapoType = (fullType: string): string => {
    const typeMapping: Record<string, string> = {
      'TYPES.VEHICLE.AUTOMOBILE': 'auto',
      'TYPES.VEHICLE.MOTORCYCLE': 'moto',
      'TYPES.VEHICLE.TRUCK': 'camion',
      'TYPES.VEHICLE.BUS': 'bus',
      'TYPES.VEHICLE.MACHINERY': 'maquinaria',
      'TYPES.VEHICLE.NAUTIC': 'nautico',
    }

    return typeMapping[fullType] || 'auto'
  }

  useEffect(() => {
    const fetchModelsAsync = async () => {
      if (formData.brand) {
        const yapoType = getYapoType(formData.type)
        const models = await yapoModels(yapoType, formData.brand)
        setModels(models)

        // Si el modelo que viene en el vehiculo (formData) no está en la lista, entonces
        // lo limpiamos - BUT NOT during draft loading
        if (!isDraftLoading) {
          // Case-insensitive model search
          const hay = models.find((c) => c.label.toLowerCase() === formData.model?.toLowerCase())

          if (!hay && formData.model) {
            setFormData((prev) => ({ ...prev, model: '' }))
          }
        }
      } else {
        setModels([])
      }
    }
    fetchModelsAsync()
  }, [formData.brand, formData.type, isDraftLoading])

  // Handle region sync when regions are loaded and we have a pending localization
  useEffect(() => {
    if (regions.length > 0 && tempLocalizationRef.current) {
      const localizationValue = tempLocalizationRef.current

      // Try to find regionId by region name
      const region = regions.find((r) => r.name === localizationValue)

      if (region) {
        setBranchRegionId(region.regionId)
        // Sync formData.localization with the found region name
        setFormData((prev) => ({ ...prev, localization: region.name }))
        tempLocalizationRef.current = null // Clear after processing
      } else {
        // Try case-insensitive search
        const regionCaseInsensitive = regions.find((r) => r.name.toLowerCase() === localizationValue.toLowerCase())

        if (regionCaseInsensitive) {
          setBranchRegionId(regionCaseInsensitive.regionId)
          setFormData((prev) => ({ ...prev, localization: regionCaseInsensitive.name }))
          tempLocalizationRef.current = null
        } else {
          // Try partial match
          const regionPartial = regions.find((r) => r.name.toLowerCase().includes(localizationValue.toLowerCase()))

          if (regionPartial) {
            setBranchRegionId(regionPartial.regionId)
            setFormData((prev) => ({ ...prev, localization: regionPartial.name }))
            tempLocalizationRef.current = null
          } else {
            // Keep the value for potential future processing or fallback
            setBranchRegionId(localizationValue)
            setFormData((prev) => ({ ...prev, localization: localizationValue }))
            tempLocalizationRef.current = null
          }
        }
      }
    }
  }, [regions])

  // Ensure formData.localization is always in sync with branchRegionId when regions are available
  useEffect(() => {
    if (regions.length > 0 && branchRegionId && !formData.localization) {
      const selectedRegion = regions.find((r) => r.regionId === branchRegionId)

      if (selectedRegion) {
        setFormData((prev) => ({ ...prev, localization: selectedRegion.name }))
      }
    }
  }, [regions, branchRegionId, formData.localization])

  // Cuando se cambia la marca
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target

    if (formData) {
      setFormData(() => {
        return {
          ...formData,
          [name]: name === 'year' ? parseInt(value, 10) : value,
        }
      })
    }
  }

  // Cuando se cambia la marca, limpiamos el modelo
  const handleBrandChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target

    if (formData) {
      setFormData(() => {
        return {
          ...formData,
          [name]: value,
          model: '', // Clear model when brand changes
        }
      })
    }
  }

  // Cuando se cambia la region
  const handleRegionChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value } = e.target
    setBranchRegionId(value)
    setHasUserSelectedRegion(true) // Mark that user has explicitly selected a region

    // Find the region name from the regionId and save that to localization
    const selectedRegion = regions.find((r) => r.regionId === value)
    const regionName = selectedRegion ? selectedRegion.name : value
    setFormData((prev) => ({ ...prev, localization: regionName }))
  }

  // Cuando el usuario cambia el tipo (auto, moto, etc)
  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.value

    if (value && formData) {
      setFormData({ ...formData, type: value, brand: '', model: '' })
    }
  }

  // Save draft functionality
  const handleSaveDraft = async () => {
    setSaveMessage('')
    clearError()

    // Ensure formData.localization is synchronized before saving
    let dataToSave = { ...formData }

    // If user hasn't explicitly selected a region but branchRegionId has a value,
    // sync the localization field
    if (!hasUserSelectedRegion && branchRegionId && regions.length > 0) {
      const selectedRegion = regions.find((r) => r.regionId === branchRegionId)

      if (selectedRegion) {
        dataToSave = { ...dataToSave, localization: selectedRegion.name }
      }
    }

    const result = await savePublicationData(vehicle._id, 'yapo', dataToSave as unknown as Record<string, unknown>)

    if (result.success) {
      setSaveMessage('✅ Borrador guardado correctamente')
      setTimeout(() => setSaveMessage(''), 3000)
    } else {
      setSaveMessage(`❌ Error: ${result.message}`)
      setTimeout(() => setSaveMessage(''), 5000)
    }
  }

  // Auto-load draft data when page loads
  const autoLoadDraftData = async () => {
    setIsDraftLoading(true)

    const result = await loadPublicationData<YapoData>(vehicle._id, 'yapo')

    if (result.success && result.data) {
      // Merge draft data with current form data, draft takes priority
      setFormData((prev) => ({ ...prev, ...result.data }))

      // Store localization value for later processing when regions are ready
      if (result.data.localization) {
        // We'll handle region sync in a useEffect that watches regions changes
        tempLocalizationRef.current = result.data.localization
      }

      // Wait a bit to ensure formData state update completes
      // before setting isDraftLoading to false (which triggers model validation)
      setTimeout(() => {
        setIsDraftLoading(false)
      }, 200)
    } else if (!result.success) {
      // Silently fail for auto-load - don't show error messages
      console.warn('Failed to auto-load draft:', result.message)
      setIsDraftLoading(false)
    } else {
      // No draft data exists
      setIsDraftLoading(false)
    }
  }

  // Publicar en Yapo
  const handlePublishConfirm = async () => {
    setConfirmationModalOpen(false)

    // NEW: Save draft data first before publishing
    console.log('Saving draft data before publishing to Yapo...')

    // Ensure formData.localization is synchronized before saving
    let dataToSave = { ...formData }

    // If user hasn't explicitly selected a region but branchRegionId has a value,
    // sync the localization field
    if (!hasUserSelectedRegion && branchRegionId && regions.length > 0) {
      const selectedRegion = regions.find((r) => r.regionId === branchRegionId)

      if (selectedRegion) {
        dataToSave = { ...dataToSave, localization: selectedRegion.name }
      }
    }

    const saveResult = await savePublicationData(vehicle._id, 'yapo', dataToSave as unknown as Record<string, unknown>)

    if (!saveResult.success) {
      setFaltaMsg(`❌ Error al guardar datos: ${saveResult.message}`)

      return
    }

    console.log('✅ Draft data saved successfully, proceeding with Yapo publication')

    // Original publish logic (unchanged)
    const organization = userData?.organization

    if (!organization) return

    if (!formData) return

    if (!branch) return

    // Buscamos la comuna/id yapo
    const regionId = branchRegionId
    const region = await fetchRegionDetail(regionId)

    if (!region) {
      setFaltaMsg('No se encontró la region para Yapo.')

      return
    }

    if (!vehicle.pictures?.public) {
      setFaltaMsg('Vehículo no tiene imágenes públicas.')

      return
    }

    const pictures = vehicle.pictures.public
    // Reemplazamos los datos por los que el usuario
    // escribe en la pantalla

    // chequear que esté all bien: combos con valor seleccionado
    const validationErrors = validateFields([
      { name: 'type', value: formData.type, rule: Rule.REQUIRED },
      { name: 'body', value: formData.body, rule: Rule.REQUIRED },
      { name: 'brand', value: formData.brand, rule: Rule.REQUIRED },
      { name: 'model', value: formData.model, rule: Rule.REQUIRED },
      { name: 'year', value: formData.year, rule: Rule.REQUIRED },
      { name: 'fuel', value: formData.fuel, rule: Rule.REQUIRED },
      { name: 'gearbox', value: formData.gearbox, rule: Rule.REQUIRED },
      { name: 'municipality', value: branchRegionId, rule: Rule.REQUIRED },
    ])

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)

      return
    }

    // Hay un problema, que consiste en que el combo puede
    // estar SIn valor, pero el "formData.xxx" TIENE un valor,
    // que no coincide con ninguno
    // En este caso, el validationError NO funciona.
    //Asi que : Los que son Record estraemos sus keys. Los demas sus valores
    const combosToValidate = [
      { name: 'type', value: formData.type, keys: Object.keys(types) },
      { name: 'body', value: formData.body, keys: bodies.map((m) => m.body) },
      { name: 'brand', value: formData.brand, keys: brands.map((m) => m.name) },
      { name: 'year', value: formData.year.toString(), keys: years.map((m) => m.toString()) },
      { name: 'fuel', value: formData.fuel, keys: Object.keys(fuel) },
      { name: 'gearbox', value: formData.gearbox, keys: Object.keys(gearbox) },
    ]

    // Solo cuando tenemos modelos disponibles validamos contra el combo
    if (models.length > 0) {
      combosToValidate.push({ name: 'model', value: formData.model, keys: models.map((m) => m.label) })
    }
    const validationErrorsCombo = validateCombos(combosToValidate)

    if (Object.keys(validationErrorsCombo).length > 0) {
      setErrors(validationErrorsCombo)

      return
    }

    const vehicleYapo: YapoInputVehicle = {
      ...vehicle,
      ...formData,
      organization: organization,
      pictures: pictures,
      branch: { ...branch, yapoRegionId: region.yapoRegionId },
      characteristics: vehicle.characteristics || [],
    }

    const resp = await yapoPublish(vehicleYapo)

    if (resp?.ok) {
      const respSetPublished = await yapoSetPublished(vehicle._id, userData?.profile?._id ?? '')

      if (respSetPublished) {
        setFaltaMsg(`✅ Vehículo guardado y publicado exitosamente en Yapo`)
        onRefresh()
      }
    } else {
      setFaltaMsg('❌ Error al publicar en Yapo')
    }
  }
  const handlePublishClick = () => {
    setConfirmationModalOpen(true)
  }
  const handlePublishCancel = () => {
    setConfirmationModalOpen(false)
  }

  return (
    <div>
      <div className="border">
        <DisplayError msg={faltaMsg} />
      </div>
      <div className="grid grid-cols-4 gap-4">
        <SelectField
          label="Tipo"
          name="type"
          value={formData.type}
          options={types}
          handleInputChange={handleTypeChange}
          errors={errors}
        />

        <SelectField
          label="Carrocería"
          name="body"
          value={formData.body}
          options={bodies}
          keyProp="body"
          valueProp="description"
          handleInputChange={handleInputChange}
          errors={errors}
        />

        <SelectField
          label="Marca"
          name="brand"
          value={formData.brand}
          options={brands}
          keyProp="name"
          valueProp="name"
          handleInputChange={handleBrandChange}
          errors={errors}
        />
        {models.length > 0 ? (
          <SelectField
            label="Modelo"
            name="model"
            value={formData.model}
            options={models}
            keyProp="label"
            valueProp="label"
            handleInputChange={handleInputChange}
            errors={errors}
          />
        ) : (
          <InputField
            label="Modelo"
            name="model"
            value={formData?.model}
            type="text"
            handleInputChange={handleInputChange}
            errors={errors}
          />
        )}
        <SelectYapoYear
          label="Año"
          name="year"
          value={formData.year}
          options={years}
          handleInputChange={handleInputChange}
          errors={errors}
        />
        <SelectField
          label="Combustible"
          name="fuel"
          value={formData.fuel}
          options={fuel}
          handleInputChange={handleInputChange}
          errors={errors}
        />
        <SelectField
          label="Transmisión"
          name="gearbox"
          value={formData.gearbox}
          options={gearbox}
          handleInputChange={handleInputChange}
          errors={errors}
        />
        <SelectField
          label={'Comuna'}
          name={'municipality'}
          value={branchRegionId}
          options={regions}
          keyProp="regionId"
          valueProp="name"
          handleInputChange={handleRegionChange}
          errors={errors}
        />
      </div>
      <InputField
        label="Comentarios Generales"
        name="comments"
        value={formData?.comments}
        type="text"
        handleInputChange={handleInputChange}
      />

      <div>{errorYapoBrands?.message}</div>
      <div>{errorYapoTypes?.message}</div>
      <div>{errorYapoBodies?.message}</div>
      <div>{errorYapoModels?.name}</div>
      <div>{errorYapoYears?.message}</div>
      <div>{errorYapoFuel?.message}</div>
      <div>{errorYapoGearbox?.message}</div>
      <div>{errorRegion?.message}</div>
      <div>{errorMunicipalities?.message}</div>
      <DisplayError msg={errorYapoPublish?.message} />
      <DisplayError msg={errorYapoSetPublished?.message} />

      <div className="flex justify-center mt-4">
        <Info message={infoMessage} trigger={Date.now()} onTimeout={() => setInfoMessage('')} noCenter={true} />
      </div>
      {(isDraftLoading || !minimumTimeElapsed || !externalApisComplete) &&
        !(saveMessage && saveMessage.startsWith('✅')) && (
          <div className="flex justify-center mt-2">
            <div className="text-sm font-bold text-blue-600">📝 Cargando datos...</div>
          </div>
        )}
      {(saveMessage || saveError) && (
        <div className="flex justify-center mt-2">
          <div className={`text-sm ${saveMessage && saveMessage.startsWith('✅') ? 'text-green-600' : 'text-red-600'}`}>
            {saveMessage || saveError}
          </div>
        </div>
      )}
      <div className="flex justify-center mt-4 gap-4">
        <ButtonAction
          label={savingLoading ? 'Guardando...' : 'Guardar'}
          handleButtonClick={handleSaveDraft}
          disabled={disabled || savingLoading}
        />

        <ButtonAction label={`Publicar en Yapo`} handleButtonClick={handlePublishClick} disabled={disabled} />
      </div>
      {confirmationModalOpen && (
        <ConfirmationModal
          message="¿Estás seguro de publicar en Yapo?"
          onConfirm={handlePublishConfirm}
          onCancel={handlePublishCancel}
        />
      )}
    </div>
  )
}

const SelectYapoYear: React.FC<{
  label: string
  name: string
  value?: number
  options: number[] | undefined
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
  errors?: { [key: string]: string }
}> = ({ label, name, value, options, handleInputChange, errors }) => {
  // Convert array to record
  const optionsRecord: Record<string, string> = (options ?? []).reduce(
    (acc, option) => {
      acc[option.toString()] = option.toString()

      return acc
    },
    {} as Record<string, string>
  )

  return (
    <SelectField
      label={label}
      name={name}
      value={value?.toString()}
      options={optionsRecord}
      handleInputChange={handleInputChange}
      errors={errors}
    />
  )
}
export default Yapo
